/**
 * FastPDV - Driver de balanca via Web Serial API
 * --------------------------------------------------------------------------
 * Protocolos suportados (continuos em ASCII):
 *  - Toledo Prix3 / MGV5 / 9094  : "ST,GS,  0.182kg\r\n" ou "ST,GS,   182g\r\n"
 *  - Filizola CS/Platina         : "+0001.234kg\r" ou "P0001.234 kg\r"
 *  - Urano POP-S                 : " 01.234kg\r\n"
 *
 * A maioria emite atualizacoes continuas ~10x/s. Se o modelo precisar de
 * solicitacao (comando "P\r"), o chamador pode usar requestWeight().
 *
 * Referencia:
 *  - https://developer.mozilla.org/en-US/docs/Web/API/Web_Serial_API
 *  - Manual Toledo/Filizola/Urano
 */

export interface BalancaReading {
  /** peso em kg (3 casas) */
  kg: number;
  /** true se o peso ainda esta oscilando (nao estavel) */
  oscilando: boolean;
  /** raw ASCII recebido (debug) */
  raw: string;
}

export interface BalancaOptions {
  baudRate?: number;
  dataBits?: 7 | 8;
  stopBits?: 1 | 2;
  parity?: 'none' | 'even' | 'odd';
  onReading: (r: BalancaReading) => void;
  onError?: (err: Error) => void;
}

export function isBalancaSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

type SerialNavigator = Navigator & {
  serial: {
    requestPort: (opts?: { filters?: Array<{ usbVendorId?: number }> }) => Promise<SerialPortLike>;
  };
};

interface SerialPortLike {
  open: (init: {
    baudRate: number;
    dataBits?: 7 | 8;
    stopBits?: 1 | 2;
    parity?: 'none' | 'even' | 'odd';
  }) => Promise<void>;
  close: () => Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
}

/**
 * Extrai valor numerico em kg de uma string ASCII da balanca.
 * Aceita formatos:
 *  - "0001.234kg"  -> 1.234
 *  - "  0.182kg"   -> 0.182
 *  - "   182g"     -> 0.182
 *  - "ST,GS,   1.234kg" -> 1.234 (Toledo)
 *  - "+1.234kg" / "-0.020kg" (tara)
 */
export function parseBalancaLine(line: string): { kg: number; oscilando: boolean } | null {
  if (!line) return null;

  const sanitized = line.replace(/[\x00-\x1f]+/g, ' ').trim();
  if (!sanitized) return null;

  // Status Toledo: "US" = instavel (unstable). "ST" = estavel. "OV" = overload.
  const oscilando = /\bUS\b|\bMOV\b/i.test(sanitized);

  // 1) Match numero em kg
  const kgMatch = sanitized.match(/([-+]?\d+(?:\.\d+)?)\s*kg/i);
  if (kgMatch) {
    const n = Number(kgMatch[1]);
    if (Number.isFinite(n)) return { kg: Math.max(0, Math.abs(n)), oscilando };
  }

  // 2) Grams -> convert
  const gMatch = sanitized.match(/([-+]?\d+(?:\.\d+)?)\s*g\b/i);
  if (gMatch) {
    const n = Number(gMatch[1]);
    if (Number.isFinite(n)) return { kg: Math.max(0, Math.abs(n) / 1000), oscilando };
  }

  // 3) Fallback: ultimo numero decimal da linha (assume kg)
  const genericMatch = sanitized.match(/[-+]?\d+(?:\.\d+)?/g);
  if (genericMatch && genericMatch.length > 0) {
    const n = Number(genericMatch[genericMatch.length - 1]);
    if (Number.isFinite(n) && n < 1000) {
      return { kg: Math.max(0, Math.abs(n)), oscilando };
    }
  }

  return null;
}

export interface BalancaSession {
  port: SerialPortLike;
  stop: () => Promise<void>;
  requestWeight: () => Promise<void>;
}

export async function connectBalanca(opts: BalancaOptions): Promise<BalancaSession> {
  if (!isBalancaSupported()) {
    throw new Error('NAVEGADOR_SEM_WEB_SERIAL');
  }

  const nav = navigator as SerialNavigator;
  const port = await nav.serial.requestPort();
  await port.open({
    baudRate: opts.baudRate ?? 9600,
    dataBits: opts.dataBits ?? 8,
    stopBits: opts.stopBits ?? 1,
    parity: opts.parity ?? 'none',
  });

  let stopped = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const decoder = new TextDecoder();
  let buffer = '';

  const loop = async () => {
    try {
      while (!stopped && port.readable) {
        reader = port.readable.getReader();
        try {
          while (!stopped) {
            const { value, done } = await reader.read();
            if (done) break;
            if (!value) continue;
            buffer += decoder.decode(value, { stream: true });

            // Separa por CR, LF ou CRLF - balancas variam
            const lines = buffer.split(/\r\n|\r|\n/);
            buffer = lines.pop() ?? '';
            for (const raw of lines) {
              const parsed = parseBalancaLine(raw);
              if (parsed) opts.onReading({ raw, ...parsed });
            }
          }
        } finally {
          try {
            reader?.releaseLock();
          } catch {
            // noop
          }
          reader = null;
        }
      }
    } catch (err) {
      opts.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void loop();

  return {
    port,
    requestWeight: async () => {
      if (!port.writable) return;
      const writer = port.writable.getWriter();
      try {
        await writer.write(new TextEncoder().encode('P\r'));
      } finally {
        writer.releaseLock();
      }
    },
    stop: async () => {
      stopped = true;
      try {
        await reader?.cancel();
      } catch {
        // noop
      }
      try {
        await port.close();
      } catch {
        // noop
      }
    },
  };
}
